import React, { useContext, useState, useEffect, useRef } from "react";
import { ProjectContext } from "../../context/project_context";
import TimecodeInput from "../../components/TimecodeInput";
import debounce from 'lodash/debounce';
import { countDuplicateEnglishTranscripts } from "../../utils/english_transcript_duplicates";

const ProjectCorrectEnglish = () => {
  const {
    project,
    org_id,
    project_id,
    updateProject,
    fetchEnglishTranscripts,
    english_transcripts,
    handleSaveCorrectEnglishSentenceTranscript,
    updateEnglishTranscriptTimecodes,
    removeDuplicateEnglishTranscripts,
    // Media playback
    activeTranscriptId,
    autoScrollEnabled,
    seekToTranscript
  } = useContext(ProjectContext);
  
  const [loading, setLoading] = useState(false);
  const [editedTranslations, setEditedTranslations] = useState({});
  const [savingStates, setSavingStates] = useState({});

  // Create a memoized debounced function for each transcript
  const [debouncedFunctions] = useState(() => ({}));

  // Refs for auto-scrolling
  const rowRefs = useRef({});

  useEffect(() => {
    if (project?.id) {
      fetchEnglishTranscripts();
    }
  }, [project?.id]);

  // Auto-scroll to active row
  useEffect(() => {
    if (autoScrollEnabled && activeTranscriptId && rowRefs.current[activeTranscriptId]) {
      rowRefs.current[activeTranscriptId].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeTranscriptId, autoScrollEnabled]);

  const handleTranslationChange = (transcriptId, newTranslation) => {
    setEditedTranslations(prev => ({
      ...prev,
      [transcriptId]: newTranslation
    }));

    if (debouncedFunctions[transcriptId]) {
      debouncedFunctions[transcriptId].cancel();
    }

    if (!debouncedFunctions[transcriptId]) {
      debouncedFunctions[transcriptId] = debounce(async (translation) => {
        try {
          setSavingStates(prev => ({ ...prev, [transcriptId]: true }));
          const success = await handleSaveCorrectEnglishSentenceTranscript(transcriptId, translation);
          if (success) {
            setSavingStates(prev => ({ ...prev, [transcriptId]: false }));
          }
        } catch (error) {
          console.error('Failed to save transcript:', error);
          setSavingStates(prev => ({ ...prev, [transcriptId]: false }));
        }
      }, 1000);
    }

    debouncedFunctions[transcriptId](newTranslation);
  };

  const handleTimecodeChange = async (transcriptId, field, newValue) => {
    const transcript = english_transcripts.find(t => t.id === transcriptId);
    if (!transcript) return;

    const startTime = field === 'start_time' ? newValue : parseFloat(transcript.start_time);
    const endTime = field === 'end_time' ? newValue : parseFloat(transcript.end_time);

    if (endTime <= startTime) {
      alert('End time must be greater than start time');
      return;
    }

    try {
      setSavingStates(prev => ({ ...prev, [`timecode_${transcriptId}`]: true }));
      await updateEnglishTranscriptTimecodes(transcriptId, startTime, endTime);
    } catch (error) {
      console.error('Failed to update timecodes:', error);
      alert('Failed to update timecodes');
    } finally {
      setSavingStates(prev => ({ ...prev, [`timecode_${transcriptId}`]: false }));
    }
  };

  const handleBack = async () => {
    await updateProject({
      ...project,
      project_status: "translate_to_english"
    });
  };

  const handleComplete = async () => {
    await updateProject({
      ...project,
      project_status: "generate_tts"
    });
  };

  const handleKeyDown = (e, currentIndex) => {
    const textareas = document.querySelectorAll('.translation-textarea');
    
    switch (e.key) {
      case 'ArrowUp':
        if (currentIndex > 0) {
          textareas[currentIndex - 1].focus();
        }
        break;
      case 'ArrowDown':
        if (currentIndex < textareas.length - 1) {
          textareas[currentIndex + 1].focus();
        }
        break;
      case 'Enter':
        if (!e.shiftKey) {
          e.preventDefault();
          if (currentIndex < textareas.length - 1) {
            textareas[currentIndex + 1].focus();
          }
        }
        break;
    }
  };

  const handleRowClick = (transcript) => {
    seekToTranscript(transcript);
  };

  const duplicateCount = countDuplicateEnglishTranscripts(english_transcripts);

  const handleRemoveDuplicates = async () => {
    if (duplicateCount === 0) return;
    if (!window.confirm(
      `Remove ${duplicateCount} duplicate row(s)? Rows with the same timeframe and original text will be deleted. The earliest row in each group will be kept.`
    )) return;
    await removeDuplicateEnglishTranscripts();
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="correct-english">
      <div className="d-flex justify-content-between align-items-center">
        <h2>Correct English Translations</h2>
        <div className="d-flex gap-2 mt-3">
          <a href={`/api_super_admin/orgs/${org_id}/projects/${project_id}/taglish_srt_text`} className="btn btn-primary mr-3" target="_blank" download={`${project.name}_taglish_srt.srt`}>
            Download Taglish SRT
          </a>
          <a href={`/api_super_admin/orgs/${org_id}/projects/${project_id}/english_srt_text`} className="btn btn-primary" target="_blank" download={`${project.name}_english_srt.srt`}>
            Download English SRT
          </a>
        </div>
      </div>
      <div className="transcripts-list mt-3">
        <div className="d-flex gap-2 mb-2">
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={handleRemoveDuplicates}
            disabled={duplicateCount === 0}
          >
            Remove Duplicates ({duplicateCount})
          </button>
        </div>
        <table className="table table-bordered">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>T#</th>
              <th style={{ width: '240px' }}>Timecodes</th>
              <th>Original</th>
              <th>English Auto-Translation</th>
              <th style={{ width: '100px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {english_transcripts.map((transcript, index) => {
              const isActive = transcript.id === activeTranscriptId;
              
              return (
                <tr 
                  key={transcript.id}
                  ref={el => rowRefs.current[transcript.id] = el}
                  className={isActive ? 'table-info' : ''}
                  style={{ 
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  <td 
                    style={{ width: '50px' }}
                    onClick={() => handleRowClick(transcript)}
                  >
                    {transcript.sequence_number}
                  </td>
                  <td style={{ width: '240px' }}>
                    <div className="d-flex flex-column gap-1">
                      <div className="d-flex align-items-center gap-1">
                        <small className="text-muted" style={{ width: '35px' }}>Start:</small>
                        <TimecodeInput
                          value={parseFloat(transcript.start_time)}
                          onChange={(val) => handleTimecodeChange(transcript.id, 'start_time', val)}
                        />
                      </div>
                      <div className="d-flex align-items-center gap-1">
                        <small className="text-muted" style={{ width: '35px' }}>End:</small>
                        <TimecodeInput
                          value={parseFloat(transcript.end_time)}
                          onChange={(val) => handleTimecodeChange(transcript.id, 'end_time', val)}
                        />
                      </div>
                      {savingStates[`timecode_${transcript.id}`] && (
                        <small className="text-muted">Saving...</small>
                      )}
                    </div>
                  </td>
                  <td style={{ width: '275px' }}>{transcript.original_text}</td>
                  <td className="p-1">
                    <textarea
                      className="form-control translation-textarea border-0 rounded-0"
                      defaultValue={transcript.final_text}
                      onChange={(e) => handleTranslationChange(transcript.id, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      rows="2"
                    />
                  </td>
                  <td style={{ width: '100px' }}>
                    {(editedTranslations[transcript.id] && savingStates[transcript.id]) && (
                      <span className="text-muted small">Saving...</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex gap-2 mt-3">
        <button 
          className="btn btn-secondary"
          onClick={handleBack}
        >
          Back to Translation
        </button>
        <button
          className="btn btn-primary"
          onClick={handleComplete}
          disabled={loading}
        >
          Complete English Correction
        </button>
      </div>
    </div>
  );
};

export default ProjectCorrectEnglish;
